// 晌木时光导航 · 共享组件与工具
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clone, fetchConfig, getConfigState, loadConfig, resetConfig, saveConfig, subscribeConfig } from '../store/api-store.js';

const USER_NAME_TOKEN = '{{user.name}}';
const FALLBACK_TEAM_NAME = '木源团队';
const ANONYMOUS_GREETING_AUDIENCE = '木源的同事们！';
const ICON_DISPLAY_SCALE = {
  // These brand marks have generous transparent padding in their source artwork,
  // so they need a small optical boost to read the same size as square app icons.
  '1688-1145qi1.png': 1.16,
  'logo-9edv5y.png': 1.16,
  'deepseek-f7b76j.png': 1.14,
  'logo-76cwzv.png': 1.18,
};

function resolveTeamName(team, user, fallback = FALLBACK_TEAM_NAME) {
  const source = String(team || '').trim() || fallback;
  const userName = String(user?.name || user?.username || '').trim() || fallback;
  return source.replaceAll(USER_NAME_TOKEN, userName);
}

/* ── derive a stable monogram + tile tint from a name ── */
function monogram(name) {
  const s = name.trim();
  const cjk = s.match(/[\u4e00-\u9fff]/);
  if (cjk && s.indexOf(cjk[0]) <= 2) {
    // first CJK char (skip a leading latin word like "AI 工具" -> use AI handled below)
    const firstChar = s[0];
    if (/[\u4e00-\u9fff]/.test(firstChar)) return firstChar;
  }
  const tok = (s.match(/[A-Za-z0-9]+/) || [s[0]])[0];
  if (/^[0-9]/.test(tok)) return tok.slice(0, 2);
  if (tok.length <= 2) return tok.toUpperCase();
  return tok[0].toUpperCase();
}
function hashHue(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}
// A curated, harmonious tint — low chroma, consistent lightness, hue from hash.
function tileTint(name, dark) {
  const hue = hashHue(name);
  if (dark) return { bg: `oklch(0.42 0.07 ${hue})`, fg: `oklch(0.94 0.03 ${hue})` };
  return { bg: `oklch(0.93 0.045 ${hue})`, fg: `oklch(0.42 0.09 ${hue})` };
}

/* ── monogram tile ── */
function Tile({ name, size = 40, radius = 11, dark, bold = 700 }) {
  const t = tileTint(name, dark);
  const mg = monogram(name);
  const fs = mg.length >= 2 ? size * 0.36 : size * 0.44;
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flex: '0 0 auto',
      background: t.bg, color: t.fg, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontWeight: bold, fontSize: fs, letterSpacing: '-0.01em',
      fontFamily: '"Hanken Grotesk","Noto Sans SC",sans-serif',
    }}>{mg}</div>
  );
}

/* ── local icon tile; never synthesizes remote favicon URLs at render time ── */
function ItemIcon({ item, size = 40, radius = 11, dark }) {
  const src = item?.icon || '';
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (src && !failed) {
    const frameRadius = Math.max(radius, Math.round(size * 0.28));
    const iconName = src.split('/').pop();
    const displayScale = ICON_DISPLAY_SCALE[iconName] || 1;
    return (
      <div
        className="ws-item-icon"
        style={{
          '--item-icon-size': `${size}px`,
          '--item-icon-radius': `${frameRadius}px`,
          '--item-icon-padding': `${Math.max(3, Math.round(size * 0.08))}px`,
          '--item-icon-scale': displayScale,
        }}
      >
        <img className="ws-item-icon-img" src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      </div>
    );
  }
  return <Tile name={item ? item.name : '?'} size={size} radius={radius} dark={dark} />;
}

/* ── live clock ── */
function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let id;
    const tick = () => {
      clearTimeout(id);
      setNow(new Date());
      id = setTimeout(tick, 60000 - Date.now() % 60000);
    };
    tick();
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearTimeout(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);
  return now;
}
const WK = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
function fmtTime(d) { return [d.getHours(), d.getMinutes()].map((n) => String(n).padStart(2, '0')).join(':'); }
function fmtDate(d) { return `${d.getMonth() + 1} 月 ${d.getDate()} 日 · ${WK[d.getDay()]}`; }
function greeting(d) {
  const h = d.getHours();
  if (h < 6) return '夜深了';
  if (h < 11) return '上午好';
  if (h < 13) return '中午好';
  if (h < 18) return '下午好';
  return '晚上好';
}

function welcomeGreeting(d, team, user) {
  if (!user) return `${greeting(d)}，${ANONYMOUS_GREETING_AUDIENCE}`;
  return `${greeting(d)},${resolveTeamName(team, user)}`;
}

/* ── tiny inline icons (stroke, currentColor) ── */
const Icon = {
  search: (p) => <svg viewBox="0 0 24 24" width={p.s || 18} height={p.s || 18} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" /></svg>,
  sun: (p) => <svg viewBox="0 0 24 24" width={p.s || 18} height={p.s || 18} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" /></svg>,
  moon: (p) => <svg viewBox="0 0 24 24" width={p.s || 18} height={p.s || 18} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 14.5A8 8 0 0 1 9.5 4 7 7 0 1 0 20 14.5Z" /></svg>,
  bell: (p) => <svg viewBox="0 0 24 24" width={p.s || 18} height={p.s || 18} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>,
  pin: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4h6l-1 6 3 3v2H7v-2l3-3-1-6Z" /><path d="M12 17v4" /></svg>,
  arrow: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M9 7h8v8" /></svg>,
  grid: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>,
  cloud: (p) => <svg viewBox="0 0 24 24" width={p.s || 18} height={p.s || 18} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="3.2" /><path d="M7 19h9a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 6 12.5 3.5 3.5 0 0 0 7 19Z" /></svg>,
  check: (p) => <svg viewBox="0 0 24 24" width={p.s || 14} height={p.s || 14} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4.5 4.5L19 7" /></svg>,
  dot: (p) => <svg viewBox="0 0 8 8" width={p.s || 7} height={p.s || 7}><circle cx="4" cy="4" r="4" fill="currentColor" /></svg>,
  chevron: (p) => <svg viewBox="0 0 24 24" width={p.s || 14} height={p.s || 14} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>,
  plus: (p) => <svg viewBox="0 0 24 24" width={p.s || 14} height={p.s || 14} fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  minus: (p) => <svg viewBox="0 0 24 24" width={p.s || 14} height={p.s || 14} fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M5 12h14" /></svg>,
  cmd: (p) => <svg viewBox="0 0 24 24" width={p.s || 14} height={p.s || 14} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3Z" /></svg>,
  star: (p) => <svg viewBox="0 0 24 24" width={p.s || 14} height={p.s || 14} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9Z" /></svg>,
  starFill: (p) => <svg viewBox="0 0 24 24" width={p.s || 14} height={p.s || 14} fill="currentColor"><path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9Z" /></svg>,
  close: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  gear: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3.1" /><path d="M19.4 14.5a1.5 1.5 0 0 0 .3 1.65l.05.05a2 2 0 1 1-2.8 2.8l-.05-.05a1.5 1.5 0 0 0-2.55 1.06V21a2 2 0 1 1-4 0v-.08A1.5 1.5 0 0 0 7.15 19.5l-.05.05a2 2 0 1 1-2.8-2.8l.05-.05A1.5 1.5 0 0 0 3.7 14.5H3.6a2 2 0 1 1 0-4h.08A1.5 1.5 0 0 0 4.5 7.15l-.05-.05a2 2 0 1 1 2.8-2.8l.05.05A1.5 1.5 0 0 0 9.5 4.6V3.6a2 2 0 1 1 4 0v.08A1.5 1.5 0 0 0 16.85 4.5l.05-.05a2 2 0 1 1 2.8 2.8l-.05.05A1.5 1.5 0 0 0 20.3 9.5h.1a2 2 0 1 1 0 4h-.08a1.5 1.5 0 0 0-1.37 1Z" /></svg>,
  ext: (p) => <svg viewBox="0 0 24 24" width={p.s || 14} height={p.s || 14} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 5h5v5M19 5l-8 8M11 5H6a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5" /></svg>,
  user: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.6" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></svg>,
  grip: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="currentColor"><circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" /></svg>,
  logout: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h3M16 17l5-5-5-5M21 12H9" /></svg>,
  layout: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></svg>,
  reset: (p) => <svg viewBox="0 0 24 24" width={p.s || 16} height={p.s || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" /></svg>,
};

/* ── brand mark: a stacked wood-grain "木" monogram (configurable) ── */
function Brand({ size = 36, dark, text = '木', img = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
      <div style={{
        width: size, height: size, borderRadius: 10, flex: '0 0 auto', overflow: 'hidden',
        background: img ? 'transparent' : (dark ? 'oklch(0.62 0.12 52)' : 'oklch(0.55 0.12 50)'),
        color: dark ? 'oklch(0.18 0.02 60)' : '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: size * 0.5, fontFamily: '"Noto Sans SC",sans-serif',
        boxShadow: img ? 'none' : (dark ? 'none' : '0 1px 2px oklch(0.55 0.12 50 / .35)'),
      }}>{img ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : text}</div>
    </div>
  );
}

/* ── shared config hook (front + admin) ── */
function useConfig({ enabled = true } = {}) {
  const [cfg, setCfg] = useState(() => loadConfig());
  const [state, setState] = useState(() => getConfigState());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const cfgRef = useRef(cfg);
  const pending = useRef(false);
  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeConfig((next, status) => {
      cfgRef.current = next;
      setCfg(next);
      setState(status);
    });
  }, [enabled]);
  const write = useCallback(async (operation) => {
    if (pending.current || !getConfigState().synced) {
      const error = new Error(pending.current ? '正在保存，请稍候' : '请先重新载入最新配置再保存，当前修改尚未保存');
      setSaveError(error);
      throw error;
    }
    pending.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      return await operation();
    } catch (error) {
      setSaveError(error);
      throw error;
    } finally {
      pending.current = false;
      setSaving(false);
    }
  }, []);
  const update = useCallback((mutator, options = {}) => write(() => {
    const base = clone(cfgRef.current);
    const next = typeof mutator === 'function' ? mutator(base) : clone(mutator);
    // A form must keep the revision at which its draft began, even when a
    // background refresh delivers newer data while that form is being edited.
    if (options.revision != null) next.revision = options.revision;
    return saveConfig(next);
  }), [write]);
  const reset = useCallback((revision) => write(() => resetConfig(revision)), [write]);
  const reload = useCallback(async () => {
    const next = await fetchConfig();
    setSaveError(null);
    return next;
  }, []);
  return [cfg, update, state.ready, { ...state, saving, saveError, reload, reset }];
}

const THEME_OPTIONS = [
  { value: 'warm', label: '暖色' },
  { value: 'soft', label: '浅色' },
  { value: 'dark', label: '深色' },
];

function normalizeThemeMode(value) {
  if (value === true || value === 'dark') return 'dark';
  if (value === 'soft') return 'soft';
  // Historical configs stored the original warm palette as "light".
  if (value === 'warm' || value === 'light' || value === false || value == null) return 'warm';
  return 'warm';
}

function isDarkTheme(value) {
  return normalizeThemeMode(value) === 'dark';
}

function nextThemeMode(value) {
  const current = normalizeThemeMode(value);
  const order = ['warm', 'soft', 'dark'];
  return order[(order.indexOf(current) + 1) % order.length];
}

/* ── shared warm / soft / dark token set (wood accent preserved) ── */
function theme(mode) {
  const current = normalizeThemeMode(mode);
  if (current === 'dark') return {
    app: 'oklch(0.205 0.008 60)', panel: 'oklch(0.24 0.008 62)', card: 'oklch(0.27 0.009 62)',
    cardHover: 'oklch(0.30 0.01 62)', border: 'oklch(0.34 0.012 62)', borderSoft: 'oklch(0.30 0.01 62)',
    text: 'oklch(0.93 0.006 70)', text2: 'oklch(0.74 0.01 65)', text3: 'oklch(0.58 0.01 65)',
    accent: 'oklch(0.70 0.12 52)', accentText: 'oklch(0.20 0.02 60)', accentSoft: 'oklch(0.32 0.05 52)',
    shadow: '0 1px 3px oklch(0 0 0 / .4)', shadowUp: '0 8px 28px oklch(0 0 0 / .5)',
  };
  if (current === 'soft') return {
    app: 'oklch(0.992 0.002 82)', panel: 'oklch(1 0 0)', card: 'oklch(1 0 0)',
    cardHover: 'oklch(0.987 0.003 82)', border: 'oklch(0.925 0.006 72)', borderSoft: 'oklch(0.955 0.004 76)',
    text: 'oklch(0.285 0.01 58)', text2: 'oklch(0.50 0.01 60)', text3: 'oklch(0.66 0.008 62)',
    accent: 'oklch(0.55 0.12 50)', accentText: 'oklch(1 0 0)', accentSoft: 'oklch(0.972 0.018 55)',
    shadow: '0 1px 2px oklch(0.5 0.04 60 / .055)', shadowUp: '0 12px 30px oklch(0.4 0.04 60 / .10)',
  };
  return {
    app: 'oklch(0.984 0.006 78)', panel: 'oklch(0.995 0.003 80)', card: 'oklch(1 0 0)',
    cardHover: 'oklch(0.985 0.006 75)', border: 'oklch(0.905 0.008 72)', borderSoft: 'oklch(0.94 0.006 72)',
    text: 'oklch(0.29 0.012 58)', text2: 'oklch(0.51 0.012 60)', text3: 'oklch(0.64 0.01 62)',
    accent: 'oklch(0.55 0.12 50)', accentText: 'oklch(1 0 0)', accentSoft: 'oklch(0.955 0.028 55)',
    shadow: '0 1px 2px oklch(0.5 0.05 60 / .08)', shadowUp: '0 12px 32px oklch(0.4 0.05 60 / .14)',
  };
}

function cssVars(mode) {
  const t = theme(mode);
  return {
    '--ws-app': t.app, '--ws-panel': t.panel, '--ws-card': t.card, '--ws-card-hover': t.cardHover,
    '--ws-border': t.border, '--ws-border-soft': t.borderSoft, '--ws-text': t.text,
    '--ws-text2': t.text2, '--ws-text3': t.text3, '--ws-accent': t.accent,
    '--ws-accent-text': t.accentText, '--ws-accent-soft': t.accentSoft,
    '--ws-shadow': t.shadow, '--ws-shadow-up': t.shadowUp,
  };
}

export { USER_NAME_TOKEN, THEME_OPTIONS, normalizeThemeMode, isDarkTheme, nextThemeMode, resolveTeamName, monogram, tileTint, hashHue, Tile, ItemIcon, useClock, fmtTime, fmtDate, greeting, welcomeGreeting, Icon, Brand, WK, theme, cssVars, useConfig };
